import { useState } from 'react';
import { Alert, Form } from 'react-bootstrap';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { AuthCard } from '../components/common/AuthCard';
import { FormAlert, PasswordField, SubmitButton, vPassword } from '../components/form';
import { post, statusOf } from '../lib/api';
import { passwordFeedbackKey } from '../lib/passwordFeedback';
import { Paths } from '../routes/paths';
import { usePageTitle } from '../lib/usePageTitle';

const schema = z
    .object({
        password: vPassword,
        confirmPassword: z.string().min(1, 'validation.required'),
    })
    .refine((d) => d.password === d.confirmPassword, {
        message: 'validation.passwordsMustMatch',
        path: ['confirmPassword'],
    });
type Values = z.infer<typeof schema>;

export interface SetPasswordPageProps {
    endpoint: string;
    /** Query-string parameter that carries the token (`token` vs `emailToken`). */
    tokenParam: string;
    /** Payload key for the email (`Email` vs `email`) — the two backends differ. */
    emailKey: 'Email' | 'email';
    title: string;
    subtitle: string;
    submitLabel: string;
}

export function SetPasswordPage({
    endpoint,
    tokenParam,
    emailKey,
    title,
    subtitle,
    submitLabel,
}: SetPasswordPageProps) {
    const { t } = useTranslation();
    usePageTitle(title);
    const methods = useForm<Values>({
        resolver: zodResolver(schema),
        defaultValues: { password: '', confirmPassword: '' },
        mode: 'onSubmit',
        reValidateMode: 'onChange',
    });
    const [serverError, setServerError] = useState('');
    const [successKey, setSuccessKey] = useState('');

    async function onSubmit(values: Values) {
        setServerError('');
        const sp = new URLSearchParams(window.location.search);
        const payload: Record<string, unknown> = {
            password: values.password,
            repeatPassword: values.confirmPassword,
            EmailToken: sp.get(tokenParam),
        };
        payload[emailKey] = sp.get('email');
        try {
            const res = await post(endpoint, payload);
            setSuccessKey(passwordFeedbackKey(res.status));
        } catch (err) {
            setServerError(t(passwordFeedbackKey(statusOf(err))));
        }
    }

    if (successKey) {
        return (
            <AuthCard title={title}>
                <Alert variant="success">{t(successKey)}</Alert>
                <Link to={Paths.login} className="btn btn-primary">
                    {t('common.backToLogin')}
                </Link>
            </AuthCard>
        );
    }

    return (
        <AuthCard title={title} subtitle={subtitle}>
            <FormProvider {...methods}>
                <FormAlert onClose={() => setServerError('')}>{serverError || undefined}</FormAlert>
                <Form noValidate onSubmit={methods.handleSubmit(onSubmit)}>
                    <PasswordField
                        name="password"
                        label={t('setPassword.password')}
                        autoComplete="new-password"
                        required
                        helpText={t('validation.passwordWeak')}
                    />
                    <PasswordField
                        name="confirmPassword"
                        label={t('setPassword.confirmPassword')}
                        autoComplete="new-password"
                        required
                    />
                    <SubmitButton loading={methods.formState.isSubmitting}>{submitLabel}</SubmitButton>
                </Form>
            </FormProvider>
        </AuthCard>
    );
}
